import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  Loader2, Send, Hash, AtSign, Bell, ArrowLeft, Users2, Circle, Smile, Plus, Users, Menu, X, Paperclip, FileText, Download
} from 'lucide-react';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import NewChatDialog from '@/components/NewChatDialog';
import CalendarPeek from '@/components/CalendarPeek';

const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '🚀', '✅', '🙏'];

const initials = (name) => (name || '?').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();

const timeLabel = (iso) => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const same = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return same ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${time}`;
  } catch { return ''; }
};

const renderContent = (text, myName) => {
  // Highlight @Name mentions inline
  const parts = text.split(/(@[A-Za-z0-9._\- ]+?)(?=[\s.,!?]|$)/g);
  return parts.map((p, i) => {
    if (p.startsWith('@')) {
      const isMe = myName && p.slice(1).toLowerCase().trim() === myName.toLowerCase().trim();
      return (
        <span
          key={i}
          className={`px-1 rounded font-semibold ${isMe ? 'bg-amber-200 text-amber-900' : 'bg-sky-100 text-sky-800'}`}
        >
          {p}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
};

const EmployeeChat = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();

  // Absolute backend URL for <img> / <a href> — includes ?auth=<jwt> so browser can render private files
  const backendBase = process.env.REACT_APP_BACKEND_URL || '';
  const authToken = typeof window !== 'undefined' ? (localStorage.getItem('littlelearners-token') || '') : '';
  const withAuth = (url) => {
    if (!url) return url;
    const abs = url.startsWith('http') ? url : `${backendBase}${url}`;
    if (!authToken) return abs;
    return `${abs}${abs.includes('?') ? '&' : '?'}auth=${encodeURIComponent(authToken)}`;
  };

  const downloadAttachment = async (url, filename) => {
    // Fetch as blob via axios (keeps auth header) so the browser truly saves the file
    // instead of opening it inline — mobile browsers ignore <a download> on cross-origin URLs.
    try {
      // Backend returns url as `/api/files/{id}`, but axios `api` client already has baseURL `${BACKEND}/api`.
      // Strip the leading `/api` so we don't end up with `/api/api/files/...`.
      const relPath = url.startsWith('/api/') ? url.slice(4) : url;
      const res = await api.get(relPath, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke the blob URL after the click has been processed
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1500);
    } catch (err) {
      toast.error('Download failed. Please try again.');
    }
  };

  const [bootLoading, setBootLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dmConversations, setDmConversations] = useState([]);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [me, setMe] = useState(null);
  const [activeChannel, setActiveChannel] = useState('general');
  const [dmPeer, setDmPeer] = useState(null); // { user_id, name } when in a DM
  const isDM = activeChannel.startsWith('dm:');
  const isGroup = activeChannel.startsWith('grp:');
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [composer, setComposer] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [pendingMentions, setPendingMentions] = useState([]); // {user_id, name}
  const [mentionInbox, setMentionInbox] = useState({ mentions: [], unread_count: 0 });
  const [showInbox, setShowInbox] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]); // {file_id, url, name, mime, size}
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [lightbox, setLightbox] = useState(null); // { url, name } | null
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragCounterRef = useRef(0);

  const scrollerRef = useRef(null);
  const composerRef = useRef(null);

  // ============ INITIAL LOAD ============
  useEffect(() => {
    (async () => {
      try {
        const [chRes, emRes] = await Promise.all([
          api.get('/chat/channels'),
          api.get('/chat/employees'),
        ]);
        setChannels(chRes.data.channels || []);
        // Load my group chats
        try {
          const gRes = await api.get('/chat/groups');
          setGroups(gRes.data.groups || []);
        } catch (err) { void err; }
        // Load my DM conversations (Slack-style sidebar)
        try {
          const dRes = await api.get('/chat/dm-conversations');
          setDmConversations(dRes.data.conversations || []);
        } catch (err) { void err; }
        setEmployees(emRes.data.employees || []);
      } catch (e) {
        if (e?.response?.status === 403) {
          toast.error('Chat is restricted to employees.');
          navigate('/dashboard');
          return;
        }
        toast.error('Failed to load chat.');
      } finally {
        setBootLoading(false);
      }
    })();
  }, [api, navigate]);

  // ============ LOAD MESSAGES FOR ACTIVE CHANNEL / DM / GROUP ============
  const loadMessages = useCallback(async () => {
    setLoadingMsgs(true);
    try {
      let res;
      if (isDM && dmPeer) {
        res = await api.get(`/chat/dm/${dmPeer.user_id}`);
        setMe(res.data.me || null);
      } else if (isGroup) {
        const gid = activeChannel.slice(4);
        res = await api.get(`/chat/groups/${gid}/messages`);
        setMe(res.data.me || null);
      } else {
        res = await api.get(`/chat/messages?channel=${encodeURIComponent(activeChannel)}&limit=80`);
        setMe(res.data.me || null);
      }
      setMessages(res.data.messages || []);
    } catch {
      toast.error('Failed to load messages.');
    } finally {
      setLoadingMsgs(false);
    }
  }, [api, activeChannel, isDM, isGroup, dmPeer]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Poll messages + mentions every 6s
  const prevMentionCountRef = useRef(0);
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        let msgRes;
        if (isDM && dmPeer) {
          msgRes = await api.get(`/chat/dm/${dmPeer.user_id}`);
        } else if (isGroup) {
          const gid = activeChannel.slice(4);
          msgRes = await api.get(`/chat/groups/${gid}/messages`);
        } else {
          msgRes = await api.get(`/chat/messages?channel=${encodeURIComponent(activeChannel)}&limit=80`);
        }
        const menRes = await api.get('/chat/mentions?unread_only=false&limit=30');
        setMessages(msgRes.data.messages || []);
        const nextUnread = menRes.data.unread_count || 0;
        const nextMentions = menRes.data.mentions || [];
        // In-app fallback: if unread count grew, notify (works even without push permission)
        if (nextUnread > prevMentionCountRef.current) {
          const latest = nextMentions.find((m) => !m.read);
          if (latest) {
            toast.info(`@${latest.mention_by_name} mentioned you in #${latest.channel}`, {
              description: latest.snippet || '',
              action: { label: 'Open', onClick: () => setActiveChannel(latest.channel) },
            });
            try {
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
                new Notification(`@${latest.mention_by_name} mentioned you`, {
                  body: `#${latest.channel} · ${latest.snippet || ''}`,
                  icon: '/logo192.png',
                  tag: `mention-${latest.id}`,
                });
              }
            } catch (err) { void err; }
          }
        }
        prevMentionCountRef.current = nextUnread;
        setMentionInbox({
          mentions: nextMentions,
          unread_count: nextUnread,
        });
      } catch { /* silent */ }
    }, 6000);
    return () => clearInterval(t);
  }, [api, activeChannel, isDM, isGroup, dmPeer]);

  // Initial mentions fetch
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/chat/mentions?unread_only=false&limit=30');
        const count = res.data.unread_count || 0;
        prevMentionCountRef.current = count; // seed so first poll doesn't re-toast old mentions
        setMentionInbox({
          mentions: res.data.mentions || [],
          unread_count: count,
        });
      } catch { /* silent */ }
    })();
  }, [api]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, activeChannel]);

  // ============ COMPOSER — @ MENTION AUTOCOMPLETE ============
  const filteredMentions = useMemo(() => {
    if (!showMentions) return [];
    const q = mentionQuery.toLowerCase();
    return employees
      .filter((e) => (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [showMentions, mentionQuery, employees]);

  const onComposerChange = (val) => {
    setComposer(val);
    // Detect @token at cursor
    const idx = val.lastIndexOf('@');
    if (idx >= 0) {
      const after = val.slice(idx + 1);
      // Only open if the token has no spaces yet (still typing a name)
      if (!/\s{2,}|\n/.test(after) && after.length <= 40) {
        setShowMentions(true);
        setMentionQuery(after);
        return;
      }
    }
    setShowMentions(false);
    setMentionQuery('');
  };

  const insertMention = (emp) => {
    const idx = composer.lastIndexOf('@');
    const before = idx >= 0 ? composer.slice(0, idx) : composer;
    const next = `${before}@${emp.name} `;
    setComposer(next);
    setShowMentions(false);
    setMentionQuery('');
    setPendingMentions((prev) => {
      if (prev.find((p) => p.user_id === emp.user_id)) return prev;
      return [...prev, { user_id: emp.user_id, name: emp.name }];
    });
    composerRef.current?.focus();
  };

  const send = async () => {
    const text = composer.trim();
    if ((!text && pendingAttachments.length === 0) || sending) return;
    // Only include mentions that actually still appear in the text
    const activeMentions = pendingMentions
      .filter((p) => text.includes(`@${p.name}`))
      .map((p) => p.user_id);
    const attachments = pendingAttachments.map((a) => ({
      file_id: a.file_id, url: a.url, name: a.name, mime: a.mime, size: a.size,
    }));
    setSending(true);
    try {
      let res;
      if (isDM && dmPeer) {
        res = await api.post('/chat/dm', { to_user_id: dmPeer.user_id, content: text, attachments });
      } else if (isGroup) {
        const gid = activeChannel.slice(4);
        res = await api.post(`/chat/groups/${gid}/messages`, { content: text, attachments });
      } else {
        res = await api.post('/chat/messages', {
          channel: activeChannel,
          content: text,
          mentions: activeMentions,
          attachments,
        });
      }
      setMessages((prev) => [...prev, res.data.message]);
      setComposer('');
      setPendingMentions([]);
      setPendingAttachments([]);
    } catch {
      toast.error('Failed to send message.');
    } finally {
      setSending(false);
    }
  };

  const handleFilesPicked = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} is over 25MB — skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        try {
          const res = await api.post('/files/upload?scope=chat', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const d = res.data || {};
          setPendingAttachments((prev) => [...prev, {
            file_id: d.id,
            url: d.url,
            name: d.original_filename || file.name,
            mime: d.content_type || file.type || 'application/octet-stream',
            size: d.size || file.size || 0,
          }]);
        } catch (err) {
          toast.error(`Upload failed: ${file.name}`);
        }
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePendingAttachment = (fileId) => {
    setPendingAttachments((prev) => prev.filter((a) => a.file_id !== fileId));
  };

  // ============ DRAG-AND-DROP FILE UPLOAD ============
  const onDragEnter = (e) => {
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingFile(true);
  };
  const onDragOver = (e) => {
    if (!e.dataTransfer || !Array.from(e.dataTransfer.types || []).includes('Files')) return;
    e.preventDefault(); // required to allow drop
  };
  const onDragLeave = (e) => {
    if (!e.dataTransfer) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDraggingFile(false);
  };
  const onDrop = async (e) => {
    if (!e.dataTransfer) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingFile(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      await handleFilesPicked(e.dataTransfer.files);
    }
  };

  // Close lightbox on ESC
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const openDM = (emp) => {
    setDmPeer({ user_id: emp.user_id, name: emp.name });
    setActiveChannel(`dm:${emp.user_id}`);
    // Refresh conversation sidebar (upsert this peer to the top)
    setDmConversations((prev) => {
      const others = prev.filter((c) => c.user_id !== emp.user_id);
      return [{ user_id: emp.user_id, name: emp.name, last_message: '', last_at: new Date().toISOString() }, ...others];
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const toggleReaction = async (messageId, emoji) => {
    try {
      const res = await api.post(`/chat/messages/${messageId}/reactions`, { emoji });
      setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch (err) {
      toast.error('Failed to react');
    }
  };

  const isEmployee = !!(user?.is_admin || user?.is_staff || user?.intern_role);
  const { subscribe: subscribePush } = usePushSubscription(api, isEmployee);
  const enableChatNotifications = async () => {
    const res = await subscribePush();
    if (res.subscribed) {
      toast.success('Chat notifications enabled on this device');
      try { await api.post('/push/test'); } catch (err) { void err; }
    } else if (res.reason === 'permission-denied') {
      toast.error('Enable notifications in your browser settings to receive chat alerts.');
    } else {
      toast.info(`Could not enable notifications (${res.reason || 'unknown'})`);
    }
  };

  const openMention = async (mention) => {
    setActiveChannel(mention.channel);
    setShowInbox(false);
    try {
      await api.post(`/chat/mentions/${mention.id}/read`);
      setMentionInbox((prev) => ({
        mentions: prev.mentions.map((m) => m.id === mention.id ? { ...m, read: true } : m),
        unread_count: Math.max(0, prev.unread_count - (mention.read ? 0 : 1)),
      }));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.post('/chat/mentions/read-all');
      setMentionInbox((prev) => ({
        mentions: prev.mentions.map((m) => ({ ...m, read: true })),
        unread_count: 0,
      }));
    } catch { /* silent */ }
  };

  if (bootLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const activeChannelObj = channels.find((c) => c.key === activeChannel);
  const activeGroup = isGroup ? groups.find((g) => `grp:${g.id}` === activeChannel) : null;
  const headerTitle = isDM && dmPeer
    ? dmPeer.name
    : activeGroup
      ? activeGroup.name
      : activeChannelObj?.label || activeChannel;
  const headerSubtitle = isDM
    ? 'Direct message'
    : activeGroup
      ? `Group · ${activeGroup.member_ids?.length || 0} members`
      : activeChannelObj?.description;
  const composerPlaceholder = isDM && dmPeer
    ? `Message ${dmPeer.name}`
    : activeGroup
      ? `Message ${activeGroup.name}`
      : `Message #${activeChannel} — type @ to mention`;

  return (
    <div
      className="h-screen bg-slate-50 flex flex-col relative"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isDraggingFile && (
        <div
          className="absolute inset-0 z-[60] bg-emerald-500/20 backdrop-blur-[2px] border-4 border-dashed border-emerald-500 flex items-center justify-center pointer-events-none"
          data-testid="chat-drop-overlay"
        >
          <div className="bg-white rounded-2xl shadow-xl px-6 py-4 flex items-center gap-3 text-emerald-700 font-semibold">
            <Paperclip className="w-6 h-6" />
            Drop files to attach
          </div>
        </div>
      )}
      {/* Top bar */}
      <header className="bg-white border-b h-14 flex items-center px-4 gap-3 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(true)}
          className="md:hidden px-2"
          data-testid="chat-sidebar-open-btn"
          aria-label="Open chat menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="chat-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Hash className="w-4 h-4 text-slate-400" />
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 truncate" data-testid="chat-header-title">{headerTitle}</p>
            <p className="text-xs text-slate-400 truncate">{headerSubtitle}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowInbox((v) => !v)}
          className="relative"
          data-testid="mentions-inbox-btn"
        >
          <Bell className="w-4 h-4 mr-1" /> Mentions
          {mentionInbox.unread_count > 0 && (
            <Badge className="ml-2 bg-red-500 text-white h-5 min-w-[20px] px-1.5" data-testid="mentions-unread-badge">
              {mentionInbox.unread_count}
            </Badge>
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={enableChatNotifications}
          className="ml-2"
          title="Enable push notifications"
          data-testid="enable-push-btn"
        >
          <Bell className="w-4 h-4" />
        </Button>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Mobile drawer backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-slate-900/40 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="chat-sidebar-backdrop"
          />
        )}
        {/* Left rail: channels + employees. Slide-in drawer on mobile, static on md+ */}
        <aside
          className={`bg-white overflow-y-auto flex-shrink-0 border-r
            fixed inset-y-0 left-0 w-72 z-50 transform transition-transform duration-200 ease-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            md:relative md:translate-x-0 md:w-64 md:z-auto md:block`}
          data-testid="chat-sidebar"
        >
          <div className="p-3 border-b flex items-center justify-between md:justify-start gap-2">
            <Button
              size="sm"
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              onClick={() => { setNewChatOpen(true); setSidebarOpen(false); }}
              data-testid="new-chat-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New chat
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarOpen(false)}
              className="md:hidden px-2"
              aria-label="Close menu"
              data-testid="chat-sidebar-close-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="p-3">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Channels</p>
            <ul className="space-y-1">
              {channels.map((c) => (
                <li key={c.key}>
                  <button
                    onClick={() => { setActiveChannel(c.key); setSidebarOpen(false); }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${
                      activeChannel === c.key
                        ? 'bg-emerald-50 text-emerald-700 font-semibold'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`channel-${c.key}`}
                  >
                    <Hash className="w-3.5 h-3.5" /> {c.key}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {groups.length > 0 && (
            <div className="p-3 border-t">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Groups · {groups.length}
              </p>
              <ul className="space-y-1" data-testid="group-list">
                {groups.map((g) => (
                  <li key={g.id}>
                    <button
                      onClick={() => { setActiveChannel(`grp:${g.id}`); setSidebarOpen(false); }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${
                        activeChannel === `grp:${g.id}`
                          ? 'bg-emerald-50 text-emerald-700 font-semibold'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`group-${g.id}`}
                    >
                      <Users className="w-3.5 h-3.5" />
                      <span className="truncate flex-1">{g.name}</span>
                      <span className="text-[10px] text-slate-400">{g.member_ids?.length || 0}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dmConversations.length > 0 && (
            <div className="p-3 border-t">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
                <AtSign className="w-3 h-3" /> Direct Messages · {dmConversations.length}
              </p>
              <ul className="space-y-1" data-testid="dm-conversations-list">
                {dmConversations.map((c) => (
                  <li key={c.user_id}>
                    <button
                      onClick={() => { openDM({ user_id: c.user_id, name: c.name }); setSidebarOpen(false); }}
                      className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${
                        activeChannel === `dm:${c.user_id}`
                          ? 'bg-emerald-50 text-emerald-700 font-semibold'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`dm-conv-${c.user_id}`}
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center font-bold text-[10px] shrink-0">
                        {(c.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm truncate">{c.name}</p>
                        {c.last_message && (
                          <p className="text-[10px] text-slate-400 truncate">{c.last_message}</p>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="p-3 border-t">
            <CalendarPeek />
          </div>
          <div className="p-3 border-t">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
              <Users2 className="w-3 h-3" /> Team · {employees.length}
            </p>
            <ul className="space-y-1">
              {employees.map((e) => (
                <li key={e.user_id}>
                  <button
                    type="button"
                    onClick={() => { openDM(e); setSidebarOpen(false); }}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50"
                    data-testid={`employee-row-${e.user_id}`}
                  >
                    <Avatar className="w-6 h-6">
                      <AvatarFallback className="text-[10px] bg-slate-200 text-slate-700">
                        {initials(e.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-700 truncate">{e.name}</p>
                      <p className="text-[10px] text-slate-400 truncate">{e.role_label}</p>
                    </div>
                    <Circle className="w-2 h-2 text-emerald-500 fill-emerald-500" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Message stream */}
        <section className="flex-1 flex flex-col min-w-0 relative">
          {/* Mentions inbox drawer */}
          {showInbox && (
            <div className="absolute right-4 top-2 w-96 max-w-[95vw] z-30 bg-white border rounded-xl shadow-lg" data-testid="mentions-inbox">
              <div className="p-3 border-b flex items-center justify-between">
                <p className="font-semibold text-sm text-slate-800 flex items-center gap-2">
                  <AtSign className="w-4 h-4 text-sky-500" /> Your mentions
                </p>
                {mentionInbox.unread_count > 0 && (
                  <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="mentions-mark-all-read">
                    Mark all read
                  </Button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {mentionInbox.mentions.length === 0 && (
                  <p className="p-6 text-center text-sm text-slate-400">No mentions yet — you&apos;re in the clear.</p>
                )}
                {mentionInbox.mentions.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => openMention(m)}
                    className={`w-full text-left p-3 border-b hover:bg-slate-50 ${m.read ? 'opacity-60' : ''}`}
                    data-testid={`mention-item-${m.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-800">
                        {m.mention_by_name} <span className="text-slate-400 font-normal">in #{m.channel}</span>
                      </p>
                      <span className="text-[10px] text-slate-400">{timeLabel(m.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{m.snippet}</p>
                    {!m.read && <Badge className="mt-1 bg-sky-500 text-white text-[10px]">new</Badge>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-scroller">
            {loadingMsgs && messages.length === 0 && (
              <div className="flex justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}
            {messages.length === 0 && !loadingMsgs && (
              <Card className="max-w-md mx-auto mt-8">
                <CardContent className="p-6 text-center">
                  <Hash className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="font-semibold text-slate-700">This is the start of #{activeChannel}</p>
                  <p className="text-sm text-slate-500 mt-1">Say hi to the team. Use <span className="font-mono bg-slate-100 px-1 rounded">@</span> to mention a teammate.</p>
                </CardContent>
              </Card>
            )}
            {messages.map((m, i) => {
              const prev = messages[i - 1];
              const showHeader = !prev || prev.sender_id !== m.sender_id
                || (new Date(m.created_at) - new Date(prev.created_at) > 5 * 60 * 1000);
              const iMentioned = (m.mentions || []).some((x) => x.user_id === me?.user_id);
              return (
                <div
                  key={m.id}
                  className={`flex gap-3 ${iMentioned ? 'bg-amber-50 -mx-2 px-2 py-1 rounded-md border-l-4 border-amber-400' : ''}`}
                  data-testid={`message-${m.id}`}
                >
                  <div className="w-9 flex-shrink-0">
                    {showHeader && (
                      <Avatar className="w-9 h-9">
                        <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-semibold">
                          {initials(m.sender_name)}
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {showHeader && (
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-sm text-slate-800">{m.sender_name}</span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">{m.sender_role}</span>
                        <span className="text-xs text-slate-400">{timeLabel(m.created_at)}</span>
                      </div>
                    )}
                    <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
                      {renderContent(m.content, me?.name)}
                    </p>
                    {(m.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid={`attachments-${m.id}`}>
                        {(m.attachments || []).map((a) => (
                          a.mime && a.mime.startsWith('image/') ? (
                            <button
                              type="button"
                              key={a.file_id}
                              onClick={() => setLightbox({ url: withAuth(a.url), rawUrl: a.url, name: a.name })}
                              className="block max-w-xs rounded-lg overflow-hidden border border-slate-200 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                              data-testid={`attachment-image-${a.file_id}`}
                            >
                              <img src={withAuth(a.url)} alt={a.name} className="max-h-64 object-contain bg-slate-50" />
                            </button>
                          ) : (
                            <button
                              key={a.file_id}
                              type="button"
                              onClick={() => downloadAttachment(a.url, a.name)}
                              className="flex items-center gap-2 max-w-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 hover:bg-slate-100"
                              data-testid={`attachment-file-${a.file_id}`}
                            >
                              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                              <span className="truncate flex-1 text-left" title={a.name}>{a.name}</span>
                              <Download className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                          )
                        ))}
                      </div>
                    )}
                    {/* Reactions row */}
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {Object.entries(m.reactions || {}).map(([emoji, users]) => {
                        const iReacted = users.includes(me?.user_id);
                        return (
                          <button
                            type="button"
                            key={emoji}
                            onClick={() => toggleReaction(m.id, emoji)}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border ${
                              iReacted ? 'bg-emerald-100 border-emerald-300 text-emerald-800' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                            }`}
                            data-testid={`reaction-${m.id}-${emoji}`}
                          >
                            <span>{emoji}</span>
                            <span className="font-semibold">{users.length}</span>
                          </button>
                        );
                      })}
                      <div className="relative group">
                        <button
                          type="button"
                          className="opacity-0 group-hover:opacity-100 hover:opacity-100 inline-flex items-center rounded-full w-6 h-6 justify-center text-slate-400 hover:bg-slate-100"
                          data-testid={`add-reaction-${m.id}`}
                        >
                          <Smile className="w-3.5 h-3.5" />
                        </button>
                        <div className="hidden group-hover:flex absolute left-0 bottom-6 bg-white border rounded-full shadow px-2 py-1 gap-0.5 z-10">
                          {REACTION_EMOJIS.map((emoji) => (
                            <button
                              type="button"
                              key={emoji}
                              onClick={() => toggleReaction(m.id, emoji)}
                              className="hover:bg-slate-100 rounded-full w-7 h-7 text-sm"
                              data-testid={`react-picker-${m.id}-${emoji}`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Composer */}
          <div className="border-t bg-white p-3 relative flex-shrink-0">
            {showMentions && filteredMentions.length > 0 && (
              <div className="absolute left-3 bottom-16 w-80 bg-white border rounded-lg shadow-lg overflow-hidden z-20" data-testid="mention-picker">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 pt-2">Mention a teammate</p>
                <ul>
                  {filteredMentions.map((e) => (
                    <li key={e.user_id}>
                      <button
                        onClick={() => insertMention(e)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left"
                        data-testid={`mention-option-${e.user_id}`}
                      >
                        <Avatar className="w-7 h-7">
                          <AvatarFallback className="text-xs bg-sky-100 text-sky-700">{initials(e.name)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{e.name}</p>
                          <p className="text-[11px] text-slate-400 truncate">{e.role_label}</p>
                        </div>
                        <AtSign className="w-3 h-3 text-slate-400" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {pendingAttachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2" data-testid="pending-attachments">
                {pendingAttachments.map((a) => (
                  <div key={a.file_id} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs">
                    {a.mime.startsWith('image/') ? (
                      <img src={withAuth(a.url)} alt={a.name} className="w-8 h-8 object-cover rounded" />
                    ) : (
                      <FileText className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="truncate max-w-[160px] text-slate-700" title={a.name}>{a.name}</span>
                    <button
                      type="button"
                      onClick={() => removePendingAttachment(a.file_id)}
                      className="text-slate-400 hover:text-rose-500"
                      aria-label="Remove attachment"
                      data-testid={`remove-attachment-${a.file_id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {uploading && (
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
                  </div>
                )}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => handleFilesPicked(e.target.files)}
                className="hidden"
                accept="image/*,video/*,audio/*,application/pdf,text/plain,text/csv"
                data-testid="chat-file-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="h-10 px-2 text-slate-500 hover:text-emerald-600"
                title="Attach a file"
                data-testid="chat-attach-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </Button>
              <textarea
                ref={composerRef}
                value={composer}
                onChange={(e) => onComposerChange(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={composerPlaceholder}
                rows={1}
                className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 max-h-32"
                data-testid="chat-composer"
              />
              <Button
                onClick={send}
                disabled={sending || (!composer.trim() && pendingAttachments.length === 0)}
                className="bg-emerald-500 hover:bg-emerald-600 h-10"
                data-testid="chat-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">Enter to send · Shift+Enter for a new line · @ to mention · 📎 to attach</p>
          </div>
        </section>
      </div>

      <NewChatDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        employees={employees}
        onStartDM={(emp) => openDM(emp)}
        onGroupCreated={(g) => {
          setGroups((prev) => [g, ...prev]);
          setActiveChannel(`grp:${g.id}`);
        }}
      />

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="image-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            aria-label="Close image"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); downloadAttachment(lightbox.rawUrl, lightbox.name); }}
            className="absolute top-4 left-4 flex items-center gap-2 text-white/90 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5 text-sm"
            data-testid="lightbox-download"
          >
            <Download className="w-4 h-4" /> Download
          </button>
          <div
            className="w-full h-full overflow-auto flex items-center justify-center"
            style={{ touchAction: 'pinch-zoom' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.url}
              alt={lightbox.name || 'Attachment'}
              className="max-w-none select-none"
              style={{ maxHeight: '100%', maxWidth: '100%' }}
              draggable={false}
              data-testid="lightbox-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeChat;
